import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent } from '@shared/components/button';
import { FormFieldComponent } from '@shared/components/form-field';
import { IconComponent } from '@shared/components/icon';
import { InputComponent } from '@shared/components/input';
import { ModalService } from '@shared/components/modal';
import { SelectComponent } from '@shared/components/select';
import { TableComponent } from '@shared/components/table';
import { TabPanelComponent, TabsComponent } from '@shared/components/tabs';
import { TooltipDirective } from '@shared/directives/tooltip';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AjouterCommissionEvenementComponent } from './ajouter-commission/evenement/ajouter-commission-evenement/ajouter-commission-evenement.component';
import {
  ParametrageSpecifiqueAbonnement,
  ParametrageSpecifiqueEvenement,
  ParametrageSpecifiquePack,
} from './commissions.interface.';
import { CommissionsService } from './commissions.service';

import type { TemplateRef } from '@angular/core';
import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import type { TableColumn } from '@shared/components/table';
import type { TabConfig } from '@shared/components/tabs';
import type { RequestStatus } from '@shared/stores/features';
import { AjouterCommissionAbonnementComponent } from './ajouter-commission/abonnement/ajouter-commission-abonnement/ajouter-commission-abonnement.component';
import { AjouterCommissionPackComponent } from './ajouter-commission/packs/ajouter-commission-pack/ajouter-commission-pack.component';
import { formaterDevise } from '../../../partage/format/montant';
import { formaterPourcentage } from '../../../partage/format/pourcentage';

@Component({
  selector: 'app-commissions.route',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    FormFieldComponent,
    IconComponent,
    InputComponent,
    SelectComponent,
    TableComponent,
    TabsComponent,
    TabPanelComponent,
    TooltipDirective,
    AjouterCommissionEvenementComponent,
    AjouterCommissionAbonnementComponent,
    AjouterCommissionPackComponent,
  ],
  templateUrl: './commissions.route.component.html',
  styleUrl: './commissions.route.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showPanel', [
      state('true', style({ backdropFilter: 'blur(5px)', opacity: '1', zIndex: '2000' })),
      state('false', style({ backdropFilter: 'blur(0px)', opacity: '0', zIndex: '-10' })),
      transition('false => true', [animate('0.4s ease-out')]),
    ]),
    trigger('slider', [
      state('open', style({ width: '50%', minWidth: '50%' })),
      state('close', style({ width: '0', minWidth: '0' })),
      transition('open <=> close', [animate('400ms cubic-bezier(0.4, 0.0, 0.2, 1)')]),
    ]),
  ],
})
export class CommissionsRouteComponent {
  private commissionsService = inject(CommissionsService);
  private modal = inject(ModalService);
  nomColonneEvenement: string[] = ['nom', 'idAparte', 'date', 'regle'];
  nomColonneEvenementAvecBouton: string[] = [...this.nomColonneEvenement, 'btnModif'];

  nomColonneAbonnement: string[] = ['nom', 'idAparte', 'regle'];
  nomColonneAbonnementAvecBouton: string[] = [...this.nomColonneAbonnement, 'btnModif'];

  nomColonnePack: string[] = ['nom', 'idAparte', 'regle'];
  nomColonnePackAvecBouton: string[] = [...this.nomColonnePack, 'btnModif'];

  filtreParametrageSpecifiqueEvenementControl = new FormControl('');
  filtreParametrageSpecifiqueAbonnementControl = new FormControl('');
  filtreParametrageSpecifiquePackControl = new FormControl('');

  parametragesSpecifiquesEvenement = this.commissionsService.parametragesSpecifiquesEvenement;
  parametragesSpecifiquesAbonnement = this.commissionsService.parametragesSpecifiquesAbonnement;
  parametragesSpecifiquesPack = this.commissionsService.parametragesSpecifiquesPack;
  parametragesGlobauxClient = this.commissionsService.parametragesGlobauxClient;

  clientSelection = this.commissionsService.clientSelection;
  clients = this.commissionsService.clients;

  voirPanelEvenement = this.commissionsService.voirPanelAjoutParametrageSpecifiqueEvenement;
  voirPanelAbonnement = this.commissionsService.voirPanelAjoutParametrageSpecifiqueAbonnement;
  voirPanelPack = this.commissionsService.voirPanelAjoutParametrageSpecifiquePack;

  filtreParametrageSpecifiqueEvenement = this.commissionsService.filtreParametrageSpecifiqueEvenement;
  filtreParametrageSpecifiqueAbonnement = this.commissionsService.filtreParametrageSpecifiqueAbonnement;
  filtreParametrageSpecifiquePack = this.commissionsService.filtreParametrageSpecifiquePack;

  parametragesSpecifiquesEvenementFiltres = this.commissionsService.parametragesSpecifiquesEvenementFiltres;
  parametragesSpecifiquesAbonnementFiltres = this.commissionsService.parametragesSpecifiquesAbonnementFiltres;
  parametragesSpecifiquesPackFiltres = this.commissionsService.parametragesSpecifiquesPackFiltres;

  constructor() {
    this.filtreParametrageSpecifiqueEvenementControl.valueChanges.subscribe(value => {
      this.filtreParametrageSpecifiqueEvenement.set(value || '');
    });

    this.filtreParametrageSpecifiqueAbonnementControl.valueChanges.subscribe(value => {
      this.filtreParametrageSpecifiqueAbonnement.set(value || '');
    });

    // Le champ de recherche des packs était branché au gabarit mais à aucun signal :
    // `parametragesSpecifiquesPackFiltres` filtrait sur une chaîne toujours vide, la
    // saisie ne filtrait donc rien. Antérieur à la migration au kit
    this.filtreParametrageSpecifiquePackControl.valueChanges.subscribe(value => {
      this.filtreParametrageSpecifiquePack.set(value || '');
    });
  }

  // ── Onglets et tableaux du kit ───────────────────────────────────────────
  protected readonly onglets: TabConfig[] = [
    { id: 'evenements', label: 'Evènements' },
    { id: 'abonnements', label: 'Abonnements' },
    { id: 'packs', label: 'Packs' },
  ];

  protected readonly ongletActif = signal('evenements');

  // Les paramétrages n'ont pas d'identifiant technique commun : `idAparte` est
  // stable et unique dans chacune des trois listes
  protected readonly identifiantLigne = (row: { idAparte: string | number }): string => String(row.idAparte);

  // Les trois listes sont rechargées à chaque changement de client : le tableau reflète
  // l'état réel des requêtes. Le commentaire précédent affirmait le contraire — les listes
  // viennent bien d'appels HTTP déclenchés par `clientSelection` (cf. CommissionsService)
  protected statutChargement = computed<RequestStatus>(() =>
    this.commissionsService.chargementParametragesEnCours() ? 'pending' : 'fulfilled',
  );

  private readonly gabaritRegleEvenement =
    viewChild<TemplateRef<{ $implicit: ParametrageSpecifiqueEvenement }>>('gabaritRegleEvenement');
  private readonly gabaritRegleAbonnement =
    viewChild<TemplateRef<{ $implicit: ParametrageSpecifiqueAbonnement }>>('gabaritRegleAbonnement');
  private readonly gabaritReglePack =
    viewChild<TemplateRef<{ $implicit: ParametrageSpecifiquePack }>>('gabaritReglePack');
  private readonly gabaritActionEvenement =
    viewChild<TemplateRef<{ $implicit: ParametrageSpecifiqueEvenement }>>('gabaritActionEvenement');
  private readonly gabaritActionAbonnement =
    viewChild<TemplateRef<{ $implicit: ParametrageSpecifiqueAbonnement }>>('gabaritActionAbonnement');
  private readonly gabaritActionPack =
    viewChild<TemplateRef<{ $implicit: ParametrageSpecifiquePack }>>('gabaritActionPack');

  protected colonnesEvenement = computed<readonly TableColumn<ParametrageSpecifiqueEvenement>[]>(() => {
    const regle = this.gabaritRegleEvenement();
    const action = this.gabaritActionEvenement();
    if (!regle || !action) return [];

    return [
      { id: 'nom', header: 'Evènement', kind: 'text', value: row => row.nomEvenement },
      { id: 'idAparte', header: 'Numéro évènement', kind: 'text', value: row => String(row.idAparte) },
      { id: 'date', header: 'Date évènement', kind: 'date', value: row => new Date(row.dateEvenement) },
      { id: 'regle', header: 'Règle', kind: 'custom', template: regle },
      { id: 'btnModif', header: 'Action', headerHidden: true, align: 'end', kind: 'custom', template: action },
    ];
  });

  protected colonnesAbonnement = computed<readonly TableColumn<ParametrageSpecifiqueAbonnement>[]>(() => {
    const regle = this.gabaritRegleAbonnement();
    const action = this.gabaritActionAbonnement();
    if (!regle || !action) return [];

    return [
      { id: 'nom', header: 'Nom abonnement', kind: 'text', value: row => row.nom },
      { id: 'idAparte', header: 'Numéro abonnement', kind: 'text', value: row => String(row.idAparte) },
      { id: 'regle', header: 'Règle', kind: 'custom', template: regle },
      { id: 'btnModif', header: 'Action', headerHidden: true, align: 'end', kind: 'custom', template: action },
    ];
  });

  protected colonnesPack = computed<readonly TableColumn<ParametrageSpecifiquePack>[]>(() => {
    const regle = this.gabaritReglePack();
    const action = this.gabaritActionPack();
    if (!regle || !action) return [];

    return [
      { id: 'nom', header: 'Nom pack', kind: 'text', value: row => row.nom },
      { id: 'idAparte', header: 'Numéro pack', kind: 'text', value: row => String(row.idAparte) },
      { id: 'regle', header: 'Règle', kind: 'custom', template: regle },
      { id: 'btnModif', header: 'Action', headerHidden: true, align: 'end', kind: 'custom', template: action },
    ];
  });

  // `app-select` raisonne en clés : le CODE client sert de clé, le libellé n'est
  // qu'affiché. Deux clients peuvent porter le même libellé
  protected optionsClients = computed<SelectOption[]>(() =>
    this.clients().map(client => ({ value: client.codeClient, label: client.libelleClient })),
  );

  protected clientsSelectionnes = computed<string[]>(() => {
    const selection = this.clientSelection();
    return typeof selection === 'object' && selection ? [selection.codeClient] : [];
  });

  protected choisirClient(valeurs: readonly string[]): void {
    // La croix du sélecteur (`clearable`) émet un tableau vide. C'est le même geste que
    // le bouton « Effacer » d'avant la migration, qui appelait `clearClient()` et
    // remettait donc aussi les trois filtres de recherche à zéro : sans ce renvoi, ils
    // restaient affichés alors que plus aucun client n'était sélectionné
    if (valeurs.length === 0) {
      this.clearClient();
      return;
    }

    this.commissionsService.selectionnerClientParCode(valeurs[0]);
  }

  clearClient() {
    this.commissionsService.selectionnerClientParCode(undefined);
    this.filtreParametrageSpecifiqueEvenementControl.setValue('');
    this.filtreParametrageSpecifiqueAbonnementControl.setValue('');
    this.filtreParametrageSpecifiquePackControl.setValue('');
  }

  fermerPanelEvenement() {
    this.voirPanelEvenement.set(false);
  }

  fermerPanelAbonnement() {
    this.voirPanelAbonnement.set(false);
  }

  fermerPanelPack() {
    this.voirPanelPack.set(false);
  }

  recupererLibellePourReglePlace(minimum: number, pourcentage: number): string {
    if (pourcentage > 0) {
      return `Pour chaque place vendue, la commission web Tick&Live est de ${formaterPourcentage(pourcentage)} avec un minimum de ${formaterDevise(minimum)}.`;
    }

    return `Pour chaque place vendue, la commission web Tick&Live est de ${formaterDevise(minimum)} (fixe).`;
  }

  recupererLibellePourRegleAbonnement(minimum: number, pourcentage: number): string {
    if (pourcentage > 0) {
      return `Pour chaque abonnement vendu, la commission web Tick&Live est de ${formaterPourcentage(pourcentage)} avec un minimum de ${formaterDevise(minimum)}.`;
    }

    return `Pour chaque abonnement vendu, la commission web Tick&Live est de ${formaterDevise(minimum)} (fixe).`;
  }

  recupererLibellePourReglePack(minimum: number, pourcentage: number): string {
    if (pourcentage > 0) {
      return `Pour chaque pack vendu, la commission web Tick&Live est de ${formaterPourcentage(pourcentage)} avec un minimum de ${formaterDevise(minimum)}.`;
    }

    return `Pour chaque pack vendu, la commission web Tick&Live est de ${formaterDevise(minimum)} (fixe).`;
  }

  openDialogEvenement(parametrage: ParametrageSpecifiqueEvenement) {
    // Suppression irréversible : `danger` applique le ton et le bouton destructeur
    this.modal
      .danger({
        title: 'Supprimer la règle spécifique',
        message: `Vous êtes sur le point de supprimer la règle spécifique pour l'évènement : ${parametrage.nomEvenement}`,
        confirmLabel: 'Confirmer la suppression',
        cancelLabel: 'Annuler',
      })
      .subscribe(result => {
        if (result) {
          this.commissionsService.parametrageSpecifiqueEvenementASupprimer.set(parametrage);
        }
      });
  }

  openDialogAbonnement(parametrage: ParametrageSpecifiqueAbonnement) {
    // Suppression irréversible : `danger` applique le ton et le bouton destructeur
    this.modal
      .danger({
        title: 'Supprimer la règle spécifique',
        message: `Vous êtes sur le point de supprimer la règle spécifique pour l'abonnement : ${parametrage.nom}`,
        confirmLabel: 'Confirmer la suppression',
        cancelLabel: 'Annuler',
      })
      .subscribe(result => {
        if (result) {
          this.commissionsService.parametrageSpecifiqueAbonnementASupprimer.set(parametrage);
        }
      });
  }

  openDialogPack(parametrage: ParametrageSpecifiquePack) {
    // Suppression irréversible : `danger` applique le ton et le bouton destructeur
    this.modal
      .danger({
        title: 'Supprimer la règle spécifique',
        message: `Vous êtes sur le point de supprimer la règle spécifique pour le pack : ${parametrage.nom}`,
        confirmLabel: 'Confirmer la suppression',
        cancelLabel: 'Annuler',
      })
      .subscribe(result => {
        if (result) {
          this.commissionsService.parametrageSpecifiquePackASupprimer.set(parametrage);
        }
      });
  }
}
